/**
 * ProofStatsStep — Screen 15b (v2: Proof stats)
 * Route: /onboarding/proof-stats  |  Auth: JWT
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import OnboardingLayout from '../components/shared/OnboardingLayout';
import OnboardingChatPanel from '../components/shared/OnboardingChatPanel';
import useCMS from '../hooks/useCMS';
import { useOnboarding } from '../context/OnboardingContext';
import { useScreenViewLog } from '../utils/logger';
import { saveOnboardingStep } from '../redux/onboarding/actionCreator';
import '../styles/onboarding.css';
import { parseJsonArray } from '../utils/cmsUtils';

const CMS_KEYS = [
  'onboarding_social_proof_eyebrow',
  'onboarding_social_proof_title',
  'onboarding_social_proof_cta',
  'onboarding_social_proof_chat_bot',
  'onboarding_social_proof_chat_questions',
  'onboarding_social_proof_stephen_quote',
  'onboarding_social_proof_stephen_quote_cons',
  'onboarding_social_proof_stephen_attribution',
  'onboarding_social_proof_capterra_label',
  'onboarding_social_proof_stats',
];

const ProofStatsStep = () => {
  useScreenViewLog('proof-stats', { logExit: false });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { cms } = useCMS(CMS_KEYS);
  const {
    chatHistory,
    isChatLoading,
    sendChatMessage,
    appendChat,
    completeStep,
    setCurrentStep,
  } = useOnboarding();

  const [saving, setSaving] = useState(false);
  const greetingFired = useRef(false);

  const greetingText = cms('onboarding_social_proof_chat_bot');
  useEffect(() => {
    if (greetingFired.current) return;
    if (!greetingText) return;
    greetingFired.current = true;
    appendChat('bot', greetingText);
  }, [appendChat, greetingText]);

  const handleCta = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const { ok } = await dispatch(saveOnboardingStep('proof-stats'));
      if (!ok) {
        console.log('Something went wrong — please try again.');
        return;
      }
      completeStep('proof-stats');
      setCurrentStep('proof-stats');
      navigate('/onboarding/booking-apply');
    }
    catch {
      console.log('Something went wrong — please try again.');
    }
    finally {
      setSaving(false);
    }
  };

  const preloadedQuestions = parseJsonArray(cms('onboarding_social_proof_chat_questions'));

  const chatPanel = useMemo(() => (
    <OnboardingChatPanel
      chatHistory={chatHistory}
      onSendMessage={sendChatMessage}
      isLoading={isChatLoading}
      preloadedQuestions={preloadedQuestions}
    />
  ), [chatHistory, sendChatMessage, isChatLoading]);

  const stats = parseJsonArray(cms('onboarding_social_proof_stats'));

  return (
    <OnboardingLayout
      showHeader
      rightPanelMode="chat"
      rightPanelContent={chatPanel}
      currentSection="basics"
    >
      <div className="hg-step-centered hg-social-proof__centered">
        <div className="hg-step-centered__inner hg-step-centered__inner--social">
          <p className="hg-step-centered-eyebrow sc-prog__lbl" dangerouslySetInnerHTML={{ __html: cms('onboarding_social_proof_eyebrow') }} />
          <h1 className="hg-step-centered-title hg-social-proof__title" dangerouslySetInnerHTML={{ __html: cms('onboarding_social_proof_title') }} />

          <div className="hg-social-proof__card">
            <div className="hg-social-proof__header">
              <div className="hg-social-proof__stars-row">
                <span className="hg-social-proof__stars">{'★★★★★'}</span>
                <span className="hg-social-proof__rating">5.0</span>
              </div>
              <span className="hg-social-proof__platform-capterra" dangerouslySetInnerHTML={{ __html: cms('onboarding_social_proof_capterra_label') }} />
            </div>
            <>
              <p className="hg-social-proof__quote">
                <strong>Pros:</strong>{' '}<span dangerouslySetInnerHTML={{ __html: cms('onboarding_social_proof_stephen_quote') }} />
              </p>
              <p className="hg-social-proof__quote hg-social-proof__quote--last">
                <strong>Cons:</strong>{' '}<span dangerouslySetInnerHTML={{ __html: cms('onboarding_social_proof_stephen_quote_cons') }} />
              </p>
              <p className="hg-social-proof__attribution" dangerouslySetInnerHTML={{ __html: cms('onboarding_social_proof_stephen_attribution') }} />
            </>
            <div className="hg-social-proof__divider" />
            <div className="hg-social-proof__stats">
              {stats.map((stat, i) => (
                <div key={i} className="hg-social-proof__stat">
                  <div className={`hg-social-proof__stat-value hg-social-proof__stat-value--g${i + 1}`}>{stat.value}</div>
                  <div className="hg-social-proof__stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="hg-step-cta-row hg-step-cta-row--mt">
            <button
              type="button"
              onClick={handleCta}
              disabled={saving}
              className="hg-common-btn"
              dangerouslySetInnerHTML={{ __html: cms('onboarding_social_proof_cta') }}
            />
          </div>

        </div>
      </div>
    </OnboardingLayout>
  );
};

export default ProofStatsStep;
